// Schemas for routes-whatsapp.ts (browser-safe zod). WhatsApp Cloud API through Meta directly (Embedded Signup) or
// Kapso (PRD §2, §5, §7, §9, §13).
import { z } from 'zod';
import { CapabilityReportSchema, MessageSchema } from './api.js';
import { ConnectionStatusSchema, PermissionStatusSchema, WhatsAppModeSchema } from './enums.js';

const Id = z.string().min(1).max(64);
const Iso = z.string();

export const WhatsAppSetupModeSchema = WhatsAppModeSchema;
export type WhatsAppSetupMode = z.infer<typeof WhatsAppSetupModeSchema>;

/** Capability keys of a WhatsApp Cloud API connection (Meta or Kapso). Each one is `unverified` until proven by a real provider fact. */
export const WHATSAPP_CAPABILITIES = ['identity', 'billing_ready', 'webhook_subscribed', 'templates_access', 'send', 'receive', 'coexistence_echo'] as const;
export type WhatsAppCapability = (typeof WHATSAPP_CAPABILITIES)[number];

export const WHATSAPP_CAPABILITY_LABELS: Record<WhatsAppCapability, string> = {
  identity: 'Cuenta y número de WhatsApp',
  billing_ready: 'Facturación de Meta lista',
  webhook_subscribed: 'Eventos del proveedor (webhooks)',
  templates_access: 'Acceso a plantillas',
  send: 'Envío real comprobado',
  receive: 'Recepción real comprobada',
  coexistence_echo: 'Ecos de la app de WhatsApp Business',
};

/** Honest warnings shown before and after connecting in coexistence mode (PRD §2, Kapso coexistence notes). */
export const COEXISTENCE_WARNINGS = [
  'La coexistencia es menos estable que un número dedicado: puede desconectarse y requerir reconexión.',
  'El historial de chats es limitado: no garantizamos importar conversaciones anteriores.',
  'Algunas acciones de la app (llamadas, ciertos cambios de contacto) no son visibles para el sistema.',
  'Los mensajes que envíes desde la app pausan la IA en esa conversación.',
] as const;

/**
 * Coexistence through Meta (Embedded Signup with the WhatsApp Business app number). What we know from Meta's
 * documentation — limits are marked «por confirmar» until a real account proves them (docs/connectors.md).
 */
export const META_COEXISTENCE_NOTES = [
  'La app de WhatsApp Business sigue funcionando en tu teléfono: puedes seguir contestando desde ahí.',
  'Los mensajes que envíes desde la app aparecen en LIA como enviados por una persona y pausan la IA en esa conversación.',
  'Al conectar, Meta puede compartir hasta 6 meses de chats anteriores si lo aceptas en la app; se importan como historial sin activar la IA (por confirmar).',
  'Debes seguir usando la app (Meta pide abrirla con regularidad, al menos cada 14 días) o la conexión puede desactivarse (por confirmar).',
  'Con coexistencia, la app desactiva algunas funciones: listas de difusión, mensajes temporales, ver una vez y ubicación en tiempo real (por confirmar).',
  'Disponible solo en los países que Meta habilitó para coexistencia; el ritmo de envío por API es menor que en un número dedicado (por confirmar).',
] as const;

// ───────── Templates ─────────

export const WhatsAppTemplateVariableSchema = z.object({
  key: z.string(),
  component: z.enum(['header', 'body']),
  example: z.string().nullable(),
});
export type WhatsAppTemplateVariable = z.infer<typeof WhatsAppTemplateVariableSchema>;

/** Template button as the provider defines it. `dynamic` = needs a parameter at send time (URL suffix, coupon code). */
export const WHATSAPP_TEMPLATE_BUTTON_TYPES = ['quick_reply', 'url', 'phone_number', 'copy_code', 'other'] as const;
export const WhatsAppTemplateButtonSchema = z.object({
  type: z.enum(WHATSAPP_TEMPLATE_BUTTON_TYPES),
  text: z.string(),
  /** URL buttons: the URL pattern as approved (may contain {{1}}). */
  url: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  /** Approved example (full URL for a dynamic URL, the code for copy-code). */
  example: z.string().nullable(),
  dynamic: z.boolean(),
});
export type WhatsAppTemplateButton = z.infer<typeof WhatsAppTemplateButtonSchema>;

export const WHATSAPP_TEMPLATE_BUTTON_LABELS: Record<WhatsAppTemplateButton['type'], string> = {
  quick_reply: 'Respuesta rápida',
  url: 'Enlace',
  phone_number: 'Llamar',
  copy_code: 'Copiar código',
  other: 'Otro botón',
};

/** Provider statuses (Meta) with their Spanish label; unknown statuses are shown as reported. */
export const WHATSAPP_TEMPLATE_STATUS_LABELS: Record<string, string> = {
  APPROVED: 'Aprobada',
  PENDING: 'En revisión',
  REJECTED: 'Rechazada',
  PAUSED: 'Pausada',
  DISABLED: 'Deshabilitada',
  IN_APPEAL: 'En apelación',
  PENDING_DELETION: 'Por eliminar',
  LIMIT_EXCEEDED: 'Límite excedido',
};

export const WHATSAPP_TEMPLATE_CATEGORY_LABELS: Record<string, string> = {
  MARKETING: 'Marketing',
  UTILITY: 'Utilidad',
  AUTHENTICATION: 'Autenticación',
};

export const WhatsAppTemplateSchema = z.object({
  name: z.string(),
  language: z.string(),
  category: z.string(),
  /** Provider status as reported (APPROVED, PENDING, REJECTED, PAUSED, DISABLED…). */
  status: z.string(),
  parameterFormat: z.enum(['positional', 'named']),
  headerType: z.enum(['none', 'text', 'image', 'video', 'document', 'location']),
  headerText: z.string().nullable(),
  bodyText: z.string(),
  footerText: z.string().nullable(),
  variables: z.array(WhatsAppTemplateVariableSchema),
  /** Buttons (empty for templates synced before buttons were parsed: sync again to see them). */
  buttons: z.array(WhatsAppTemplateButtonSchema).default([]),
  /** Provider components exactly as returned (HEADER/BODY/FOOTER/BUTTONS/…). */
  components: z.array(z.record(z.string(), z.json())).default([]),
  /** Only approved templates without unsupported parts can be sent. */
  sendable: z.boolean(),
  unsendableReason: z.string().nullable(),
  businessAccountId: z.string().nullable(),
});
export type WhatsAppTemplate = z.infer<typeof WhatsAppTemplateSchema>;

export const TemplateVariablesSchema = z.record(z.string().max(60), z.string().max(1000));

export const WhatsAppTemplateListSchema = z.object({ items: z.array(WhatsAppTemplateSchema), syncedAt: Iso.nullable() });

// ───────── Workspace template catalog (every WhatsApp connection of the workspace) ─────────

const boolParam = z.union([z.boolean(), z.enum(['true', 'false', '1', '0'])]).transform((v) => v === true || v === 'true' || v === '1');

export const WhatsAppTemplateCatalogQuerySchema = z.object({
  status: z.string().max(40).optional(),
  category: z.string().max(40).optional(),
  language: z.string().max(15).optional(),
  /** Name, header, body or footer text. */
  q: z.string().max(120).optional(),
  sendableOnly: boolParam.optional(),
  connectionId: Id.optional(),
});
export type WhatsAppTemplateCatalogQuery = z.input<typeof WhatsAppTemplateCatalogQuerySchema>;

export const WhatsAppCatalogConnectionSchema = z.object({
  connectionId: Id,
  provider: z.enum(['meta', 'kapso']),
  label: z.string(),
  status: ConnectionStatusSchema,
  phones: z.array(z.object({ displayName: z.string(), phoneE164: z.string().nullable() })),
  /** null = never synced. */
  syncedAt: Iso.nullable(),
  templateCount: z.number().int(),
  /** never_synced · synced · error (the last sync failed; templates shown are from the previous successful sync, if any). */
  syncState: z.enum(['never_synced', 'synced', 'error']),
  syncError: z.string().nullable(),
});
export type WhatsAppCatalogConnection = z.infer<typeof WhatsAppCatalogConnectionSchema>;

export const WhatsAppCatalogTemplateSchema = WhatsAppTemplateSchema.extend({
  connectionId: Id,
  connectionLabel: z.string(),
  /** First number of the connection (display only). */
  phoneE164: z.string().nullable(),
});
export type WhatsAppCatalogTemplate = z.infer<typeof WhatsAppCatalogTemplateSchema>;

export const WhatsAppTemplateCatalogSchema = z.object({
  /** no_connection: no WhatsApp connection · never_synced: connected but no sync yet · ready: at least one sync. */
  state: z.enum(['no_connection', 'never_synced', 'ready']),
  connections: z.array(WhatsAppCatalogConnectionSchema),
  items: z.array(WhatsAppCatalogTemplateSchema),
  /** Templates before filters. */
  total: z.number().int(),
  /** Values present in the catalog (before filters), for the filter selects. */
  facets: z.object({ statuses: z.array(z.string()), categories: z.array(z.string()), languages: z.array(z.string()) }),
});
export type WhatsAppTemplateCatalog = z.infer<typeof WhatsAppTemplateCatalogSchema>;

export const WhatsAppTemplateSyncAllResultSchema = z.object({
  results: z.array(z.object({ connectionId: Id, label: z.string(), ok: z.boolean(), templateCount: z.number().int().nullable(), error: z.string().nullable() })),
});
export type WhatsAppTemplateSyncAllResult = z.infer<typeof WhatsAppTemplateSyncAllResultSchema>;

export const TemplatePreviewSchema = z.object({
  templateName: z.string().min(1).max(512),
  language: z.string().min(2).max(15),
  variables: TemplateVariablesSchema.default({}),
});
export const TemplatePreviewResultSchema = z.object({
  text: z.string(),
  missing: z.array(z.string()),
  errors: z.array(z.string()),
  sendable: z.boolean(),
});

// ───────── Connection overview ─────────

export const WhatsAppEndpointSchema = z.object({
  id: Id,
  displayName: z.string(),
  phoneE164: z.string().nullable(),
  mode: WhatsAppSetupModeSchema.nullable(),
  status: ConnectionStatusSchema,
  healthMessage: z.string().nullable(),
  qualityRating: z.string().nullable(),
  /** Meta facts (null = not reported / Kapso). */
  verifiedName: z.string().nullable(),
  messagingLimitTier: z.string().nullable(),
  nameStatus: z.string().nullable(),
  /** Ready for sending (connection + number + send capability verified). */
  sendReady: z.boolean(),
  /** Ready for unattended AI replies / routines (coexistence also needs verified echoes). */
  automationReady: z.boolean(),
});

export const WhatsAppTestSchema = z.object({
  to: z.string(),
  kind: z.enum(['text', 'template']),
  status: z.string(),
  sentAt: Iso,
  failureReason: z.string().nullable(),
});

export const ReminderTemplateSchema = z.object({
  name: z.string().min(1).max(512),
  language: z.string().min(2).max(15),
  /** Template variable key → appointment field. */
  variables: z.record(z.string().max(60), z.enum(['contact_name', 'appointment_date', 'appointment_time', 'location', 'project_name'])).default({}),
});
export type ReminderTemplate = z.infer<typeof ReminderTemplateSchema>;

export const WhatsAppTransportSchema = z.enum(['meta', 'kapso']);
export type WhatsAppTransport = z.infer<typeof WhatsAppTransportSchema>;

export const WhatsAppHistorySyncSchema = z.object({
  /** requested | failed | receiving | declined */
  state: z.string(),
  detail: z.string().nullable(),
  requestedAt: Iso.nullable(),
  chunksReceived: z.number().int(),
  messagesImported: z.number().int(),
  progress: z.number().nullable(),
  lastChunkAt: Iso.nullable(),
});

export const WhatsAppConnectionDetailSchema = z.object({
  id: Id,
  /** Which adapter of the WhatsApp port this connection uses. */
  provider: WhatsAppTransportSchema,
  label: z.string(),
  status: ConnectionStatusSchema,
  healthMessage: z.string().nullable(),
  requestedMode: WhatsAppSetupModeSchema.nullable(),
  customerLinked: z.boolean(),
  setupPending: z.object({ expiresAt: Iso.nullable(), mode: WhatsAppSetupModeSchema }).nullable(),
  capabilities: CapabilityReportSchema,
  capabilitiesCheckedAt: Iso.nullable(),
  endpoints: z.array(WhatsAppEndpointSchema),
  templateCount: z.number().int(),
  templatesSyncedAt: Iso.nullable(),
  webhookLastEventAt: Iso.nullable(),
  test: WhatsAppTestSchema.nullable(),
  reminderTemplate: ReminderTemplateSchema.nullable(),
  warnings: z.array(z.string()),
  /** Meta: the customer's WhatsApp Business Account (never the token). */
  account: z.object({ wabaId: z.string(), name: z.string().nullable(), appSubscribed: z.boolean() }).nullable(),
  /** Meta coexistence: chat history / contacts sync state. */
  historySync: WhatsAppHistorySyncSchema.nullable(),
});
export type WhatsAppConnectionDetail = z.infer<typeof WhatsAppConnectionDetailSchema>;

const TransportAvailabilitySchema = z.object({ available: z.boolean(), reason: z.string().nullable() });

export const WhatsAppOverviewSchema = z.object({
  /** Any transport can be connected (Meta or Kapso). */
  available: z.boolean(),
  unavailableReason: z.string().nullable(),
  providers: z.object({
    /** webhookUrl: the app-level callback to configure in the Meta app (WhatsApp → Configuration); not a secret. */
    meta: TransportAvailabilitySchema.extend({ webhookUrl: z.string() }),
    kapso: TransportAvailabilitySchema,
  }),
  /** Honest channel state used by routines/agent (null = usable). */
  channelReason: z.string().nullable(),
  connections: z.array(WhatsAppConnectionDetailSchema),
});
export type WhatsAppOverview = z.infer<typeof WhatsAppOverviewSchema>;

export const StartWhatsAppSetupSchema = z.object({
  mode: WhatsAppSetupModeSchema,
  /** Reconnect an existing connection instead of creating a new one. */
  connectionId: Id.optional(),
});
export const StartWhatsAppSetupResultSchema = z.object({ connectionId: Id, url: z.string().url(), expiresAt: Iso.nullable() });

export const WhatsAppSetupCallbackQuerySchema = z
  .object({
    state: z.string().max(200).optional(),
    outcome: z.enum(['success', 'failure']).optional(),
    error_code: z.string().max(80).optional(),
  })
  .passthrough();

export const WhatsAppTestSendSchema = z
  .object({
    endpointId: Id,
    to: z.string().min(6).max(32),
    kind: z.enum(['text', 'template']),
    text: z.string().min(1).max(1000).optional(),
    templateName: z.string().min(1).max(512).optional(),
    language: z.string().min(2).max(15).optional(),
    variables: TemplateVariablesSchema.default({}),
  })
  .superRefine((v, ctx) => {
    if (v.kind === 'text' && !v.text) ctx.addIssue({ code: 'custom', path: ['text'], message: 'Escribe el texto de prueba' });
    if (v.kind === 'template' && (!v.templateName || !v.language)) ctx.addIssue({ code: 'custom', path: ['templateName'], message: 'Elige una plantilla aprobada' });
  });
export const WhatsAppTestSendResultSchema = z.object({ status: z.enum(['provider_accepted', 'failed', 'unknown']), detail: z.string().nullable(), connection: WhatsAppConnectionDetailSchema });

// ───────── Inbox composer / sending ─────────

export const ComposerAssetSchema = z.object({
  id: Id,
  title: z.string(),
  kind: z.string(),
  sizeBytes: z.number().int().nullable(),
  sendable: z.boolean(),
  reason: z.string().nullable(),
  /** Too big for WhatsApp upload: sent as a scoped link (only inside the service window). */
  linkFallback: z.boolean(),
});

export const ComposerStateSchema = z.object({
  conversationId: Id,
  channelReady: z.boolean(),
  /** Why nothing can be sent right now (null = at least templates can be sent). */
  blockedReason: z.string().nullable(),
  endpoint: z.object({ id: Id, displayName: z.string(), mode: WhatsAppSetupModeSchema.nullable() }).nullable(),
  windowOpen: z.boolean(),
  windowExpiresAt: Iso.nullable(),
  /** Free-form text/media allowed (inside the 24 h window). */
  freeFormAllowed: z.boolean(),
  freeFormReason: z.string().nullable(),
  templatesAllowed: z.boolean(),
  templatesReason: z.string().nullable(),
  permission: PermissionStatusSchema,
  suppressed: z.boolean(),
  testModeBlocked: z.boolean(),
  templates: z.array(WhatsAppTemplateSchema),
  assets: z.array(ComposerAssetSchema),
  /** Human voice notes (WA-10): free-form audio, same rules as text (24 h window, permission, suppression, test mode). */
  voiceNote: z.object({
    allowed: z.boolean(),
    reason: z.string().nullable(),
    maxSeconds: z.number().int(),
    maxBytes: z.number().int(),
    /** Types accepted for upload (browser recordings in WebM/Opus are converted to OGG/Opus by the server). */
    acceptedMimeTypes: z.array(z.string()),
  }),
});
export type ComposerState = z.infer<typeof ComposerStateSchema>;

/** Text fields of the multipart voice-note upload (file field "file"). */
export const SendVoiceNoteFieldsSchema = z.object({
  clientRequestId: z.string().min(8).max(80),
  /** Measured by the browser; the server measures OGG/WebM/M4A itself and only falls back to this value. */
  durationSeconds: z.preprocess((v) => (v === '' || v === undefined ? undefined : v), z.coerce.number().min(0).max(24 * 3600).optional()),
  source: z.enum(['recorded', 'uploaded']).default('recorded'),
  expectedVersion: z.preprocess((v) => (v === '' || v === undefined ? undefined : v), z.coerce.number().int().optional()),
});
export type SendVoiceNoteFields = z.infer<typeof SendVoiceNoteFieldsSchema>;

export const SendConversationMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('text'), text: z.string().trim().min(1).max(4096), clientRequestId: z.string().min(8).max(80), expectedVersion: z.number().int().optional() }),
  z.object({
    type: z.literal('template'),
    templateName: z.string().min(1).max(512),
    language: z.string().min(2).max(15),
    variables: TemplateVariablesSchema.default({}),
    clientRequestId: z.string().min(8).max(80),
    expectedVersion: z.number().int().optional(),
  }),
  z.object({ type: z.literal('media'), assetId: Id, caption: z.string().max(1024).optional(), clientRequestId: z.string().min(8).max(80), expectedVersion: z.number().int().optional() }),
]);
export type SendConversationMessage = z.infer<typeof SendConversationMessageSchema>;

export const SendMessageResultSchema = z.object({
  outcome: z.enum(['provider_accepted', 'failed', 'unknown', 'blocked']),
  detail: z.string().nullable(),
  message: MessageSchema.nullable(),
});
export type SendMessageResult = z.infer<typeof SendMessageResultSchema>;

export const KapsoWebhookAckSchema = z.object({ received: z.boolean(), duplicate: z.boolean(), events: z.number().int() });

// ───────── Meta Embedded Signup (Tech Provider partner flow) ─────────

export const StartMetaSignupSchema = z.object({
  /** coexistence = keep the WhatsApp Business app number (default); dedicated = new / API-only number. */
  mode: WhatsAppSetupModeSchema,
  connectionId: Id.optional(),
});

/** Public parameters for FB.login (the app secret never leaves the server). */
export const MetaSignupStartSchema = z.object({
  state: z.string(),
  appId: z.string(),
  configId: z.string(),
  graphVersion: z.string(),
  /** extras.featureType: 'whatsapp_business_app_onboarding' for coexistence, null for a new number. */
  featureType: z.string().nullable(),
  mode: WhatsAppSetupModeSchema,
  expiresAt: Iso,
});
export type MetaSignupStart = z.infer<typeof MetaSignupStartSchema>;

export const META_SIGNUP_FINISH_EVENTS = ['FINISH', 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'] as const;

export const CompleteMetaSignupSchema = z.object({
  state: z.string().min(16).max(200),
  code: z.string().min(4).max(2000),
  wabaId: z.string().regex(/^\d{5,30}$/),
  phoneNumberId: z.string().regex(/^\d{5,30}$/),
  businessId: z.string().regex(/^\d{1,30}$/).nullable().optional(),
  event: z.enum(META_SIGNUP_FINISH_EVENTS),
});
export type CompleteMetaSignup = z.infer<typeof CompleteMetaSignupSchema>;

export const MetaWebhookAckSchema = z.object({ received: z.boolean(), events: z.number().int(), duplicates: z.number().int(), quarantined: z.number().int() });

export const MetaWebhookVerifyQuerySchema = z
  .object({ 'hub.mode': z.string().max(40).optional(), 'hub.verify_token': z.string().max(400).optional(), 'hub.challenge': z.string().max(200).optional() })
  .passthrough();
